import { type ParameterType, type AttributeType } from './endpoint_metadata'

export const RFC_5988 = 'rfc_5988'

export type PaginationConfig =
  | { mode: 'no_pagination' }
  | { mode: 'standard', standard_name: string }
  | { mode: 'link', path: string }
  | { mode: 'manual', request_param: string, type: 'iteration', start?: number, step?: number }
  | { mode: 'manual', request_param: string, type: 'from_response', path: string }

interface PaginationElements {
  section?: HTMLElement
  modeRadios: HTMLInputElement[]
  input?: HTMLInputElement
  hint?: HTMLElement
  linkFields?: HTMLElement
  pathSelect?: HTMLSelectElement
  buildLinkFields?: HTMLElement
  queryParamSelect?: HTMLSelectElement
  strategySelect?: HTMLSelectElement
  iterationFields?: HTMLElement
  startInput?: HTMLInputElement
  stepInput?: HTMLInputElement
  valuePathFields?: HTMLElement
  valuePathSelect?: HTMLSelectElement
}

export class PaginationSection {
  constructor (private readonly el: PaginationElements) {}

  toggleSection (visible: boolean): void {
    this.el.section?.classList.toggle('hidden', !visible)
  }

  apply (): void {
    const mode = this.selectedMode()
    const linkSelected = mode === 'link_from_response'
    const buildLinkSelected = mode === 'build_link'
    const iterationSelected = this.selectedStrategy() === 'iteration'

    this.el.hint?.classList.toggle('hidden', linkSelected || buildLinkSelected)
    this.el.linkFields?.classList.toggle('hidden', !linkSelected)
    this.el.buildLinkFields?.classList.toggle('hidden', !buildLinkSelected)
    this.el.iterationFields?.classList.toggle('hidden', !(buildLinkSelected && iterationSelected))
    this.el.valuePathFields?.classList.toggle('hidden', !(buildLinkSelected && !iterationSelected))

    if (this.el.input == null) return

    this.el.input.value = JSON.stringify(this.currentConfig())
  }

  currentConfig (): PaginationConfig {
    return this.config(this.selectedMode())
  }

  reset (): void {
    if (this.el.modeRadios.length === 0) return

    this.el.modeRadios.forEach(radio => {
      radio.checked = radio.value === 'no_pagination'
    })

    if (this.el.pathSelect != null) this.el.pathSelect.selectedIndex = 0
    if (this.el.queryParamSelect != null) this.el.queryParamSelect.selectedIndex = 0
    if (this.el.strategySelect != null) this.el.strategySelect.value = 'iteration'
    if (this.el.startInput != null) this.el.startInput.value = ''
    if (this.el.stepInput != null) this.el.stepInput.value = ''
    if (this.el.valuePathSelect != null) this.el.valuePathSelect.selectedIndex = 0

    this.apply()
  }

  updatePathOptions (attributes: AttributeType[], label: (path: string) => string): void {
    this.populateBodyKeyOptions(this.el.pathSelect, attributes, label)
  }

  updateValuePathOptions (attributes: AttributeType[], label: (path: string) => string): void {
    this.populateBodyKeyOptions(this.el.valuePathSelect, attributes, label)
  }

  updateQueryParamOptions (params: ParameterType[]): void {
    const select = this.el.queryParamSelect
    if (select == null) return

    Array.from(select.options).slice(1).forEach(opt => opt.remove())

    params.forEach(param => {
      select.appendChild(new Option(param.name, param.name))
    })

    this.restoreInitialSelection(select, 'initialPaginationRequestParam')
  }

  private populateBodyKeyOptions (select: HTMLSelectElement | undefined, attributes: AttributeType[], label: (path: string) => string): void {
    if (select == null) return

    select.querySelector('optgroup[data-body-keys]')?.remove()

    if (attributes.length > 0) {
      const group = document.createElement('optgroup')
      group.label = select.dataset.bodyGroupLabel ?? ''
      group.dataset.bodyKeys = 'true'

      attributes.forEach(attr => {
        group.appendChild(new Option(label(attr.path), attr.path))
      })

      select.appendChild(group)
    }

    this.restoreInitialSelection(select, 'initialPaginationPath')
  }

  private restoreInitialSelection (select: HTMLSelectElement, datasetKey: string): void {
    const initial = select.dataset[datasetKey]

    const hasInitial =
      typeof initial === 'string' &&
      initial.length > 0 &&
      Array.from(select.options).some(opt => opt.value === initial)

    if (hasInitial) {
      select.value = initial
    }

    select.removeAttribute('data-' + datasetKey.replace(/[A-Z]/g, m => '-' + m.toLowerCase()))
  }

  private selectedMode (): string {
    return this.el.modeRadios.find(r => r.checked)?.value ?? 'no_pagination'
  }

  private selectedStrategy (): string {
    return this.el.strategySelect?.value ?? 'iteration'
  }

  private config (mode: string): PaginationConfig {
    if (mode === 'link_from_response') return this.linkConfig()
    if (mode === 'build_link') return this.buildLinkConfig()

    return { mode: 'no_pagination' }
  }

  private linkConfig (): PaginationConfig {
    const path = this.el.pathSelect?.value ?? ''

    if (path === RFC_5988) return { mode: 'standard', standard_name: RFC_5988 }

    return { mode: 'link', path }
  }

  private buildLinkConfig (): PaginationConfig {
    const requestParam = this.el.queryParamSelect?.value ?? ''

    if (this.selectedStrategy() === 'from_response') {
      return { mode: 'manual', request_param: requestParam, type: 'from_response', path: this.el.valuePathSelect?.value ?? '' }
    }

    const config: PaginationConfig = { mode: 'manual', request_param: requestParam, type: 'iteration' }

    if (this.el.startInput?.value != null && this.el.startInput.value !== '') {
      config.start = Number(this.el.startInput.value)
    }
    if (this.el.stepInput?.value != null && this.el.stepInput.value !== '') {
      config.step = Number(this.el.stepInput.value)
    }

    return config
  }
}
